import { notification } from 'antd';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import moment from 'moment-timezone';
import { getIntl, history, setLocale } from 'umi';

import {
  accountConfirm,
  authLogin,
  authLogout,
  authPartyLogin,
  authRefreshToken,
  sendTwoFactorAuthCode,
  validateSecurityCode,
  validateSsoClient,
} from '@/services/login';
import { authRedirect } from '@/utils/infrastructure/utilities';
import { getDefaultLocale } from '@/utils/infrastructure/utilities';
import SocketService from '@/services/socket.io';
import {
  SESSION_TYPES,
  STORAGE_KEYS,
  clearAllUserSessions,
  getCurrentSession,
  setCurrentSession,
  setItem,
  getSessionItem,
  setSessionItem,
  removeSessionItem,
  getGlobalItem,
  setGlobalItem,
  removeGlobalItem,
  setAuthority,
  subscribeToSessionEnd,
} from '@/utils/infrastructure/auth';
import { resetWhiteLabelToDefault } from '@/utils/infrastructure/whiteLabel';

const twoFactor = {
  required: false,
  codeSent: false,
  providers: [],
};

const initialState = {
  status: undefined,
  accountConfirmed: false,
  twoFactor,
  auth: false,
  operationalBlock: null,
};

// Function to restore twoFactor state from localStorage
const restoreTwoFactorState = () => {
  try {
    const savedState = getSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE);
    if (savedState) return JSON.parse(savedState);
  } catch (e) {}
  return twoFactor;
};

// Function to save twoFactor state to localStorage
const saveTwoFactorState = (state) => {
  try {
    setSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE, JSON.stringify(state));
  } catch (e) {}
};

// Function to clear twoFactor state from localStorage
const clearTwoFactorState = () => {
  try {
    removeSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE);
  } catch (e) {}
};

// Function to clear all SSO-related flags from localStorage
const clearSsoState = () => {
  try {
    localStorage.removeItem('sso-redirect-url');
    localStorage.removeItem('sso-client-id');
    localStorage.removeItem('sso-automatic-flow');
    localStorage.removeItem('sso-provider');
    localStorage.removeItem('sso-auto-disabled');
    localStorage.removeItem('sso-client-validated');
    localStorage.removeItem('sso-session-id');
  } catch (e) {}
};

const applyLocale = (payload) => {
  try {
    if (payload?.accessToken) {
      // Keep the locale chosen at login (selector / `lang` URL param) when the
      // user profile has no explicit language, so login stays in sync with the app.
      const locale = getDefaultLocale(payload, { fallbackToCurrent: true });
      setLocale(locale, false);
      moment.locale(locale);
    }
  } catch (e) {}
};

const notifySessionEnded = () => {
  try {
    const { formatMessage } = getIntl();
    notification.info({
      key: 'session-ended-elsewhere',
      message: formatMessage({ id: 'global.text.session.ended.title' }),
      description: formatMessage({ id: 'global.text.session.ended.description' }),
      duration: 8,
    });
  } catch (e) {}
};

// Client redirectUrl so the page can hand the code back to the client
const gotoSsoSuccess = (exchangeCode, redirectUrl) => {
  const params = new URLSearchParams({ code: exchangeCode });
  if (redirectUrl) params.set('redirectUrl', redirectUrl);
  history.replace(`/user/login/sso-redirect?${params.toString()}`);
};

/**
 * Handle 403 operational access block from login / 2FA / OAuth.
 * Returns payload for setOperationalBlock, or null if not an operational block.
 */
async function handleLoginOperationalBlock(error) {
  const {
    hasRestrictedBillingToken,
    isOperationalAccessBlockedResponse,
  } = require('@/utils/infrastructure/auth');
  const {
    handleOperationalAccessBlock,
  } = require('@/utils/infrastructure/communication/authUtils');

  if (!isOperationalAccessBlockedResponse(error)) return null;

  await handleOperationalAccessBlock(error, { fromRefresh: false, source: 'login' });
  return {
    authResult: error.authResult,
    code: error.code,
    message: error.message,
    status: error.status,
    hasRestrictedToken: hasRestrictedBillingToken(error),
  };
}

const Model = {
  namespace: 'auth',
  state: {
    ...initialState,
    twoFactor: restoreTwoFactorState(),
  },
  effects: {
    *login({ payload }, { call, put, select }) {
      try {
        const token = yield call(authLogin, payload);

        // Check if this is an SSO response (status 206) with exchangeCode
        if (
          (token.status === 206 || token.code === '206' || token.status === 2) &&
          token.ssoRequired &&
          token.exchangeCode
        ) {
          const redirectUrl = payload.redirectUrl || localStorage.getItem('sso-redirect-url');
          clearSsoState();
          clearTwoFactorState();
          // SSO success page shows, then hands the exchange code back to the client.
          gotoSsoSuccess(token.exchangeCode, redirectUrl);
          return { success: true };
        }

        if (payload.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(payload.accessToken),
          });
        }

        yield put({
          type: 'changeLoginStatus',
          payload: {
            ...token,
            ...(_.has(token, 'twoFactorRequired') && { ...payload }),
          },
        });

        yield put({ type: 'updateAuthorityAfterLogin', payload: token });
        applyLocale(token);

        // SSO-required login must never land on the app home; hold on a blank page.
        if (payload.isSsoFlow) {
          history.replace('/user/login/sso-redirect');
          return { success: true };
        }

        authRedirect(token);

        return { success: true };
      } catch (e) {
        const blockPayload = yield call(handleLoginOperationalBlock, e);
        if (blockPayload) {
          yield put({ type: 'setOperationalBlock', payload: blockPayload });
          return { success: false, operationalBlock: true, error: e };
        }

        const portal = yield select((o) => o.companyPortal.portal);
        history.replace({
          pathname: '/user/login',
          ...(portal && { query: { portal: portal.id } }),
        });

        // Return a result indicating failure instead of throwing
        return { success: false, error: e };
      }
    },

    *validateSsoClient({ payload }, { call, put, select }) {
      try {
        yield call(validateSsoClient, payload);
        return { success: true };
      } catch (e) {
        return { success: false, error: e };
      }
    },

    *oauth2Login({ payload }, { call, put, select }) {
      try {
        const res = yield call(authPartyLogin, payload);
        const appLoginAppId = getGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);
        removeGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);

        if (res.status === 206 || res.code === '206' || res.status === 2) {
          if (res.ssoRequired && res.exchangeCode) {
            const redirectUrl = localStorage.getItem('sso-redirect-url');
            clearSsoState();
            clearTwoFactorState();
            // SSO success page shows, then hands the exchange code back to the client.
            gotoSsoSuccess(res.exchangeCode, redirectUrl);
            return;
          }
        }

        if (appLoginAppId && !res.accessToken) {
          setGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT, appLoginAppId);
        }

        yield put({
          type: 'changeLoginStatus',
          payload: { ...res, ...(appLoginAppId && { _appLoginAppId: appLoginAppId }) },
        });

        yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        applyLocale(res);

        const ssoRedirectUrl = localStorage.getItem('sso-redirect-url');

        if (ssoRedirectUrl) {
          const ssoClientId = localStorage.getItem('sso-client-id');
          const ssoProvider = localStorage.getItem('sso-provider');

          const portalState = yield select((state) => state.companyPortal.portal);

          clearSsoState();
          clearTwoFactorState();

          try {
            const url = new URL(ssoRedirectUrl, window.location.origin);

            if (portalState) url.set('portal', portal.id);
            if (ssoClientId && !url.searchParams.get('clientId')) {
              url.searchParams.set('clientId', ssoClientId);
            }

            if (ssoProvider && !url.searchParams.get('provider')) {
              url.searchParams.set('provider', ssoProvider);
            }

            window.location.href = url.toString();
            return;
          } catch (e) {}
        }

        const appRedirectUrl = getGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);

        if (appRedirectUrl) {
          removeGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);
          window.location.href = appRedirectUrl;
          return;
        }

        // SSO-required login must never land on the app home; hold on a blank page.
        if (payload.isSsoFlow) {
          history.replace('/user/login/sso-redirect');
          return;
        }

        window.location.href = history.location?.query?.redirect || '/';
      } catch (e) {
        const blockPayload = yield call(handleLoginOperationalBlock, e);
        if (blockPayload) {
          yield put({ type: 'setOperationalBlock', payload: blockPayload });
          return;
        }

        removeGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);

        const ssoRedirectUrl = localStorage.getItem('sso-redirect-url');
        const ssoClientId = localStorage.getItem('sso-client-id');
        const ssoProvider = localStorage.getItem('sso-provider');

        localStorage.removeItem('sso-automatic-flow');
        localStorage.removeItem('sso-provider');
        removeGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);
        localStorage.setItem('sso-auto-disabled', 'true');

        const portal = yield select((state) => state.companyPortal.portal);
        const queryParams = [];

        if (portal) queryParams.push(`portal=${portal.id}`);
        if (ssoClientId) queryParams.push(`clientId=${encodeURIComponent(ssoClientId)}`);
        if (ssoRedirectUrl) queryParams.push(`redirectUrl=${encodeURIComponent(ssoRedirectUrl)}`);
        if (ssoProvider) queryParams.push(`provider=${encodeURIComponent(ssoProvider)}`);

        const query = queryParams.length ? `?${queryParams.join('&')}` : '';
        history.replace(`/user/login${query}`, history.location?.state);
      }
    },

    *sendTwoFactorCode({ payload }, { call, put, select }) {
      try {
        const data = yield select((st) => st.auth.twoFactor);
        // Only send the necessary data, avoiding circular references
        const requestData = {
          twoFactorProvider: payload.twoFactorProvider?.value || payload.twoFactorProvider,
          username: data.username,
          password: data.password,
        };

        yield call(sendTwoFactorAuthCode, requestData);
        const newTwoFactorState = { ...data, ...payload, codeSent: true };
        saveTwoFactorState(newTwoFactorState);

        yield put({ type: 'queryTwoFactorCode', payload: newTwoFactorState });
      } catch (e) {}
    },

    *validateCode({ payload }, { call, put, select }) {
      try {
        const data = yield select((st) => st.auth.twoFactor);
        const ssoClientId = localStorage.getItem('sso-client-id');
        const isSsoFlow = !!ssoClientId && !!localStorage.getItem('sso-redirect-url');

        const res = yield call(validateSecurityCode, {
          ...payload,
          ...data,
          ...(isSsoFlow && {
            isSsoFlow: true,
            ssoClientId,
          }),
        });

        // Check if this is an SSO response (status 206) with exchangeCode
        if (
          (res.status === 206 || res.code === '206' || res.status === 2) &&
          res.ssoRequired &&
          res.exchangeCode
        ) {
          const redirectUrl = localStorage.getItem('sso-redirect-url');
          clearSsoState();
          clearTwoFactorState();
          // SSO success page shows, then hands the exchange code back to the client.
          gotoSsoSuccess(res.exchangeCode, redirectUrl);
          return;
        }

        yield put({ type: 'changeLoginStatus', payload: res });
        yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        applyLocale(res);

        if (res.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(res.accessToken),
          });
        }

        removeGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);

        const ssoRedirectUrl = localStorage.getItem('sso-redirect-url');

        if (ssoRedirectUrl) {
          const ssoClientId = localStorage.getItem('sso-client-id');
          const ssoProvider = localStorage.getItem('sso-provider');

          clearSsoState();
          clearTwoFactorState();

          try {
            const url = new URL(ssoRedirectUrl, window.location.origin);
            if (ssoClientId && !url.searchParams.get('clientId')) {
              url.searchParams.set('clientId', ssoClientId);
            }
            if (ssoProvider && !url.searchParams.get('provider')) {
              url.searchParams.set('provider', ssoProvider);
            }
            window.location.href = url.toString();
            return;
          } catch (e) {}
        }

        // SSO-required login must never land on the app home; hold on a blank page.
        if (isSsoFlow) {
          history.replace('/user/login/sso-redirect');
          return;
        }

        authRedirect(res);
      } catch (e) {
        const blockPayload = yield call(handleLoginOperationalBlock, e);
        if (blockPayload) {
          yield put({ type: 'setOperationalBlock', payload: blockPayload });
        }
      }
    },

    *refreshToken({}, { call, put }) {
      try {
        const token = getSessionItem(STORAGE_KEYS.AUTH_TOKEN);
        const res = yield call(authRefreshToken, token);

        if (res.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(res.accessToken),
          });
        }

        yield put({ type: 'changeLoginStatus', payload: res });
        yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        applyLocale(res);
        yield put({ type: 'user/fetchCurrent', payload: true });
      } catch (e) {
        const { isOperationalRefreshBlockedResponse } = require('@/utils/infrastructure/auth');
        const {
          handleOperationalRefreshBlock,
        } = require('@/utils/infrastructure/communication/authUtils');

        if (isOperationalRefreshBlockedResponse(e)) {
          // Prefer the shared handler so tokens/queue/navigation stay consistent
          // with the interceptor path. Avoid generic logout ("sesión expirada").
          yield call(handleOperationalRefreshBlock, e);
          yield put({
            type: 'setOperationalBlock',
            payload: {
              authResult: e.authResult,
              code: e.code,
              message: e.message,
              status: e.status,
            },
          });
          return;
        }

        // A failed refresh means the session can no longer be recovered. Swallowing
        // the error here used to leave the user on a dead session; force a logout so
        // they are redirected to login instead.
        yield put({ type: 'logout' });
      }
    },

    *updateAuthorityAfterLogin({ payload }, {}) {
      try {
        if (payload.accessToken) {
          const user = jwtDecode(payload.accessToken);
          const roles = Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles);
          setAuthority(roles);
        }
      } catch (e) {}
    },

    *cleanupTwoFactor(o, { put }) {
      clearTwoFactorState();
      yield put({ type: 'cleanTwoFactor' });
    },

    *cleanConfirmAccount(o, { put }) {
      yield put({ type: 'cleanAccountConfirm' });
    },

    *confirmAccount({ payload }, { call, put, select }) {
      try {
        const res = yield call(accountConfirm, payload);
        if (res?.message) {
          notification.success({
            message: getIntl().formatMessage({ id: 'user.twoFactor.alert.title' }),
            description: res.message,
          });
        }
        yield put({ type: 'queryConfirm', payload: true });
      } catch (e) {
        const portal = yield select((state) => state.companyPortal.portal);
        history.replace(
          {
            pathname: '/user/login',
            ...(portal && {
              query: { portal: portal.id },
            }),
          },
          history.location?.state || portal,
        );
      }
    },

    *logout(o, { call, put, select }) {
      if (window.location.pathname !== '/user/login') {
        sessionStorage.setItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS, 'true');
        try {
          yield call(authLogout);
          yield put({ type: 'cleanup' });
          yield put({ type: 'user/clearCurrentUser' });
          yield put({ type: 'sessionNavigation/reset' });

          // Read portal from DVA state BEFORE clearing session
          const portalState = yield select((state) => state.companyPortal.portal);
          const currentSession = getCurrentSession();
          const userId = currentSession?.userId;

          // Clear ALL sessions for this user (AUTOMY + PORTAL + APP)
          if (userId) {
            clearAllUserSessions(userId);
          } else {
            removeSessionItem(STORAGE_KEYS.AUTH_TOKEN);
            removeSessionItem(STORAGE_KEYS.AUTH_USER);
            removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
            removeSessionItem(STORAGE_KEYS.AUTHORITY);
            removeSessionItem(STORAGE_KEYS.USER_TIMEZONE);
          }

          clearTwoFactorState();
          setCurrentSession(null);

          // The outgoing tenant's brand must not survive into the login screen;
          // the layout resolves the public one right after.
          resetWhiteLabelToDefault();

          const searchParams = new URLSearchParams();

          if (portalState) {
            searchParams.set('portal', portalState.id);
            yield put({
              type: 'companyPortal/auth',
              payload: _.pick(portalState, ['companyCode', 'portalCode']),
            });
          }

          if (window.location.pathname !== '/') {
            searchParams.set('redirect', window.location.href);
          }

          const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
          history.replace(`/user/login${query}`, history.location.state);
        } finally {
          sessionStorage.removeItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS);
        }
      }
    },

    /**
     * Follow a session that was ended somewhere else out of this tab.
     *
     * Credentials are shared through localStorage, so a logout in any tab has
     * already unauthenticated this one — it just keeps rendering the previous
     * user's data, with its socket still joined, until some call returns a 401.
     *
     * The session is already revoked server-side, so this must not call the
     * logout endpoint again: it only drops what this tab holds.
     */
    *sessionEndedElsewhere(o, { put, select }) {
      if (window.location.pathname.startsWith('/user/login')) return;

      const { cleanupLoginData } = require('@/utils/infrastructure/communication/authUtils');
      const { isPublicFragment } = require('@/utils/infrastructure/communication/tokenManager');

      // Anonymous surfaces (public/external forms, account confirmation) run on
      // their own request token: they neither read nor need the user session, so
      // ending it must not eject whoever is filling one in.
      if (isPublicFragment()) return;

      // Screens watch this flag to stop refetching while a session is torn down.
      sessionStorage.setItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS, 'true');

      // Read the portal before the cleanup: its config lived in the namespace the
      // other tab already wiped, so storage can no longer tell us we are on one.
      const portal = yield select((state) => state.companyPortal?.portal);

      // Joined rooms and registered listeners belong to the ended session; the
      // layout re-initialises the socket when a new one mounts it.
      try {
        SocketService.getInstance().disconnect();
      } catch (e) {}

      // Drops the leftovers of a storage cleanup done by another tab, sends this
      // one to login and — through `sessionStorage.clear()` — releases the flag
      // set above along with the session index that got us here.
      cleanupLoginData(true);
      resetWhiteLabelToDefault();

      yield put({ type: 'cleanup' });
      yield put({ type: 'user/clearCurrentUser' });
      yield put({ type: 'sessionNavigation/reset' });
      yield put({ type: 'userContext/cleanup' });
      yield put({ type: 'chat/resetChatState' });

      // A portal user belongs to the portal's own login, not to Automy's.
      if (portal) {
        yield put({
          type: 'companyPortal/auth',
          payload: _.pick(portal, ['companyCode', 'portalCode']),
        });

        const search = new URLSearchParams(history.location?.search || '');
        if (!search.get('portal')) {
          search.set('portal', portal.id);
          history.replace(`/user/login?${search.toString()}`, history.location?.state);
        }
      }

      notifySessionEnded();
    },

    *cleanup(o, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    changeLoginStatus(state, { payload }) {
      try {
        if (payload.accessToken) {
          // Successful normal token must clear any prior operational block, otherwise
          // SecurityLayout keeps redirecting to /user/login/access-blocked.
          try {
            const {
              clearOperationalAuthBlock,
              clearRestrictedBillingSession,
            } = require('@/utils/infrastructure/auth');
            clearOperationalAuthBlock();
            clearRestrictedBillingSession();
          } catch (e) {}

          const user = jwtDecode(payload.accessToken);
          const userId = user.primarysid;

          const appLoginId =
            payload._appLoginAppId || getGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);
          removeGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);

          const { _appLoginAppId, ...tokenPayload } = payload;
          const tokenData = JSON.stringify({ ...tokenPayload, issuedAt: Date.now() });

          if (appLoginId && userId) {
            setItem(
              SESSION_TYPES.APP,
              userId,
              String(appLoginId),
              STORAGE_KEYS.AUTH_TOKEN,
              tokenData,
            );
            setItem(
              SESSION_TYPES.APP,
              userId,
              String(appLoginId),
              STORAGE_KEYS.USER_TIMEZONE,
              user.timeZone,
            );
            setCurrentSession({
              type: SESSION_TYPES.APP,
              userId,
              resourceId: String(appLoginId),
            });
          } else {
            const storedSession = getCurrentSession();

            // Only reuse the stored index when it belongs to the user that just
            // authenticated and points at a context this login can write to.
            // A stale index — another user, or an APP session left behind by an
            // earlier visit to /apps/* and resurrected by tryRestoreSession —
            // would send the fresh token into a namespace the shell never reads.
            // The APP -> AUTOMY reversion that runs on the next route change then
            // finds nothing and the guard bounces the user back to /user/login.
            const canReuseSession =
              !!storedSession &&
              String(storedSession.userId) === String(userId) &&
              storedSession.type !== SESSION_TYPES.APP;

            let session = canReuseSession ? storedSession : null;

            if (!session) {
              try {
                const portalConfigRaw = sessionStorage.getItem(STORAGE_KEYS.PORTAL_CONFIG);
                if (portalConfigRaw) {
                  const portalConfig = JSON.parse(portalConfigRaw);
                  if (portalConfig?.id) {
                    session = {
                      type: SESSION_TYPES.PORTAL,
                      userId,
                      resourceId: String(portalConfig.id),
                    };
                  }
                }
              } catch (e) {}

              if (!session) {
                session = { type: SESSION_TYPES.AUTOMY, userId, resourceId: null };
              }

              setCurrentSession(session);
              if (session.type === SESSION_TYPES.PORTAL) {
                try {
                  const pendingConfig = sessionStorage.getItem(STORAGE_KEYS.PORTAL_CONFIG);
                  if (pendingConfig) {
                    setItem(
                      SESSION_TYPES.PORTAL,
                      session.userId,
                      session.resourceId,
                      STORAGE_KEYS.PORTAL_CONFIG,
                      pendingConfig,
                    );
                    sessionStorage.removeItem(STORAGE_KEYS.PORTAL_CONFIG);
                  }
                } catch (e) {}
              }
            }

            setItem(
              session.type,
              session.userId,
              session.resourceId,
              STORAGE_KEYS.AUTH_TOKEN,
              tokenData,
            );
            setItem(
              session.type,
              session.userId,
              session.resourceId,
              STORAGE_KEYS.USER_TIMEZONE,
              user.timeZone,
            );
          }

          moment.tz.setDefault(user.timeZone);
          setAuthority(Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles));
        }
      } catch (e) {}

      const newState = {
        ...state,
        status: payload.status,
        auth: !!payload.accessToken,
        type: payload.type,
        ...(payload.accessToken && { operationalBlock: null }),
        ...(payload.twoFactorRequired && {
          twoFactor: {
            ...state.twoFactor,
            required: payload.twoFactorRequired,
            providers: _.map(payload.twoFactorProviders, (str) => ({
              label: `user.twoFactor.provider.${str}`,
              value: str,
            })),
            ..._.pick(payload, ['password', 'username']),
          },
        }),
        ...(payload.accessToken && { twoFactor }),
      };

      // Save twoFactor state if required
      if (payload.twoFactorRequired) saveTwoFactorState(newState.twoFactor);
      else if (payload.accessToken) clearTwoFactorState();
      return newState;
    },

    queryTwoFactorCode(state, { payload }) {
      const newTwoFactorState = { ...state.twoFactor, ...payload };
      saveTwoFactorState(newTwoFactorState);
      return {
        ...state,
        twoFactor: newTwoFactorState,
      };
    },

    queryConfirm(state, { payload }) {
      return {
        ...state,
        accountConfirmed: payload,
      };
    },

    cleanTwoFactor(state) {
      clearTwoFactorState();
      return {
        ...state,
        twoFactor,
      };
    },

    cleanAccountConfirm(state) {
      return {
        ...state,
        accountConfirmed: false,
      };
    },

    setOperationalBlock(state, { payload }) {
      return {
        ...state,
        auth: false,
        operationalBlock: payload || null,
      };
    },

    clearOperationalBlock(state) {
      try {
        const {
          clearOperationalAuthBlock,
          clearRestrictedBillingSession,
        } = require('@/utils/infrastructure/auth');
        clearOperationalAuthBlock();
        clearRestrictedBillingSession();
      } catch (e) {}
      return {
        ...state,
        operationalBlock: null,
      };
    },

    reset() {
      clearTwoFactorState();
      setCurrentSession(null);
      return initialState;
    },
  },
  subscriptions: {
    sessionEnd({ dispatch }) {
      return subscribeToSessionEnd(() => {
        dispatch({ type: 'sessionEndedElsewhere' });
      });
    },
  },
};

export default Model;
